import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateSankeyStyleOptions } from '../../../redux/ChartPoperties/ChartControlsActions';
import { ColorSchemes } from './ColorScheme';
import Logger from '../../../Logger';
import ChartColors from './ChartColors';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';
import { OrientationRadioGroup } from '../Legend/LegendComponents/OrientationRadioGroup';
import { RootState } from '../../../redux/index';
import ColorPickerPopover from './ChartColorComponents/ColorPickerPopover';

const SankeyColorControls = ({ iconColor }: { iconColor?: string }) => {
  const dispatch = useDispatch();

  const { chartControls, tabTileProps, chartProperties } = useSelector((state: RootState) => {
    return {
      chartControls: state.chartControls,
      tabTileProps: state.tabTileProps,
      chartProperties: state.chartProperties,
    };
  });

  var propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
  let chartData = chartControls.properties[propKey].chartData
    ? chartControls.properties[propKey].chartData
    : '';

  const allThemes = useCombinedColorSchemes();
  const selectedScheme = allThemes.find(
    (scheme) => scheme.name === chartControls.properties[propKey].colorScheme,
  );

  var colorSchemes = selectedScheme?.colors || ColorSchemes[0].colors;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedItem, setSelectedItem] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [dims, setdims] = useState<any>([]);
  const [indexOfNode, setindexOfNode] = useState<any>();
  const [nameOfNode, setnameOfNode] = useState<any>();

  const savedNodesAndColors = chartControls.properties[propKey].sankeyControls.nodesAndColors;

  useEffect(() => {
    if (chartData) {
      const chartAxes = chartProperties.properties[propKey].chartAxes;
      if (chartAxes && chartAxes[1] && chartAxes[1].fields) {
        const axisFields = chartAxes[1].fields;

        // mapping out the known/existing nodes to preserve their colors
        const preservedNodes = axisFields.map((el: any) => {
          return savedNodesAndColors
            ? savedNodesAndColors.find((saved: any) => saved.nodeName === el.displayname)
            : null;
        });

        // Identify which colors are already taken by the preserved nodes
        const usedColors = new Set(
          preservedNodes.filter((n: any) => n).map((n: any) => n.nodeColor),
        );

        const mergedValues = axisFields.map((el: any, i: number) => {
          const existingNode = preservedNodes[i];

          if (existingNode) {
            return existingNode;
          } else {
            let colorCandidate = colorSchemes[i % colorSchemes.length];
            if (usedColors.has(colorCandidate)) {
              const freeColor = colorSchemes.find((c: string) => !usedColors.has(c));
              if (freeColor) {
                colorCandidate = freeColor;
              }
            }
            return { nodeName: el.displayname, nodeColor: colorCandidate };
          }
        });

        setdims(mergedValues);

        const isDifferent =
          !savedNodesAndColors ||
          savedNodesAndColors.length !== mergedValues.length ||
          !mergedValues.every(
            (val: any, index: number) =>
              savedNodesAndColors[index]?.nodeName === val.nodeName &&
              savedNodesAndColors[index]?.nodeColor === val.nodeColor,
          );

        if (isDifferent) {
          Logger('info', 'Syncing Sankey Colors', mergedValues);
          dispatch(updateSankeyStyleOptions(propKey, 'nodesAndColors', mergedValues));
        }
      }
    }
  }, [chartData, colorSchemes, savedNodesAndColors, propKey, dispatch, chartProperties]);

  const renderSmallColorBox = (
    color: string,
    onClick: (e: React.MouseEvent<HTMLElement>) => void,
  ) => (
    <div
      style={{
        height: '1rem',
        width: '30px',
        minWidth: '30px',
        backgroundColor: color,
        border: '1px solid darkgray',
        cursor: 'pointer',
        borderRadius: '2px',
        marginRight: '10px',
      }}
      onClick={onClick}
    />
  );

  const renderNodesAndColors = () => {
    if (dims.length !== 0) {
      return dims.map((item: any, i: number) => {
        return (
          <div
            key={i}
            className="optionDescription"
            style={{
              marginTop: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              paddingRight: '10px',
            }}
          >
            {renderSmallColorBox(item.nodeColor, (e) => {
              setSelectedItem('nodeColor');
              setSelectedColor(item.nodeColor);
              setindexOfNode(i);
              setnameOfNode(item.nodeName);
              setAnchorEl(e.currentTarget);
            })}
            <label style={{ fontSize: '12px', color: iconColor ? iconColor : '#404040' }}>
              {item.nodeName}
            </label>
          </div>
        );
      });
    }
    return null;
  };

  const setColorsToIndNodes = (color: string) => {
    var values = chartControls.properties[propKey].sankeyControls.nodesAndColors;

    values = values.map((el: any, i: number) => {
      if (el.nodeName === nameOfNode && i === indexOfNode) {
        return { ...el, nodeColor: color };
      }
      return el;
    });

    dispatch(updateSankeyStyleOptions(propKey, 'nodesAndColors', values));
  };

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    if (selectedItem === 'linkColor' || selectedItem === 'backgroundColor') {
      dispatch(updateSankeyStyleOptions(propKey, selectedItem, color));
    } else {
      setColorsToIndNodes(color);
    }
  };

  const isManualMode = chartControls.properties[propKey].sankeyControls.manualColor;

  return (
    <div style={{ marginTop: '20px', paddingTop: '8px', paddingLeft: '4px' }}>
      <OrientationRadioGroup
        orientation={isManualMode ? 'manual' : 'auto'}
        options={[
          { name: 'Auto', key: 'auto' },
          { name: 'Manual', key: 'manual' },
        ]}
        onChange={(key) => {
          dispatch(updateSankeyStyleOptions(propKey, 'manualColor', key === 'manual'));
        }}
        style={{ width: '94%' }}
      />

      <div>
        {isManualMode ? (
          <>
            <div style={{ marginTop: '10px', paddingRight: '10px' }}>{renderNodesAndColors()}</div>

            <div
              className="optionDescription"
              style={{
                marginTop: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingRight: '10px',
              }}
            >
              {renderSmallColorBox(
                chartControls.properties[propKey].sankeyControls.linkColor,
                (e) => {
                  setSelectedItem('linkColor');
                  setSelectedColor(chartControls.properties[propKey].sankeyControls.linkColor);
                  setAnchorEl(e.currentTarget);
                },
              )}
              <label style={{ fontSize: '12px', color: iconColor ? iconColor : '#404040' }}>
                Link Color
              </label>
            </div>

            <div
              className="optionDescription"
              style={{
                marginTop: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingRight: '10px',
              }}
            >
              {renderSmallColorBox(
                chartControls.properties[propKey].sankeyControls.backgroundColor ||
                  selectedScheme?.background ||
                  '#fff',
                (e) => {
                  setSelectedItem('backgroundColor');
                  setSelectedColor(
                    chartControls.properties[propKey].sankeyControls.backgroundColor ||
                      selectedScheme?.background ||
                      '#fff',
                  );
                  setAnchorEl(e.currentTarget);
                },
              )}
              <label style={{ fontSize: '12px', color: iconColor ? iconColor : '#404040' }}>
                Background Color
              </label>
            </div>

            <ColorPickerPopover
              isOpen={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              currentColor={selectedColor}
              onColorChange={handleColorChange}
              propKey={propKey}
              selectedMenu=""
            />
          </>
        ) : (
          <div style={{ marginTop: '-15px' }}>
            <ChartColors iconColor={iconColor} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SankeyColorControls;
